import type { DraftMachine } from "@opus-dt/shared-types";
import type {
  CompatibilityRow,
  MachineDetail as EditMachineDetail,
  ParameterDetail,
} from "@/types/line-editor.types";
import type { MachineDetail as ListMachineDetail } from "@/types/lines.types";
import type { SensorLinkRow } from "../bridge-types";
import { draftMachineToEditDetail, draftParamToDetail } from "./draft-converters";

export function selectMachine(
  isDraft: boolean,
  draftMachine: DraftMachine | undefined,
  editMachine: ListMachineDetail | undefined,
  lineId: string
): EditMachineDetail | undefined {
  if (isDraft) return draftMachine ? draftMachineToEditDetail(draftMachine) : undefined;
  if (!editMachine) return undefined;
  return {
    id: editMachine.id,
    lineId,
    code: editMachine.code,
    name: editMachine.name,
    type: editMachine.type,
    status: editMachine.status,
    positionX: 0,
    positionY: 0,
    requiresOperator: editMachine.requiresOperator,
    stoppedCostPerHour: editMachine.stoppedCostPerHour
      ? String(editMachine.stoppedCostPerHour)
      : null,
    deletedAt: null,
  };
}

export function selectParameters(
  isDraft: boolean,
  draftMachine: DraftMachine | undefined,
  editParameters: ParameterDetail[]
): ParameterDetail[] {
  if (isDraft) {
    if (!draftMachine) return [];
    return draftMachine.parameters.map((p) => draftParamToDetail(p, draftMachine.tmpId));
  }
  return editParameters;
}

export function selectSensorLinks(
  isDraft: boolean,
  draftMachine: DraftMachine | undefined
): SensorLinkRow[] {
  if (!isDraft || !draftMachine) return [];
  return draftMachine.sensorLinks.map((sl) => ({
    id: sl.tmpId,
    sensorId: sl.sensorId,
    parameterId: sl.parameterTmpId ?? null,
  }));
}

// Item 4 — sensores já vinculados a QUALQUER máquina do rascunho. Em rascunho os
// vínculos só vivem no store local (o sensor segue AVAILABLE no backend), então o
// modal de vínculo precisa desta lista para não reoferecê-los. Em edit/view o
// backend já marca o sensor como LINKED, logo retorna [].
export function selectDraftLinkedSensorIds(
  isDraft: boolean,
  draftMachines: Record<string, DraftMachine>
): string[] {
  if (!isDraft) return [];
  return Object.values(draftMachines).flatMap((m) => m.sensorLinks.map((sl) => sl.sensorId));
}

export function selectCompatibilities(
  isDraft: boolean,
  draftMachine: DraftMachine | undefined,
  editCompatibility: CompatibilityRow[],
  machineId: string | null | undefined
): CompatibilityRow[] {
  if (isDraft) {
    if (!draftMachine) return [];
    return draftMachine.compatibilities.map((c) => ({
      id: c.tmpId,
      machineId: draftMachine.tmpId,
      productId: c.productId,
      enabled: true,
      simProfile: c.simProfile ?? null,
      machine: {
        id: draftMachine.tmpId,
        code: draftMachine.basic.code,
        name: draftMachine.basic.name,
      },
      product: { id: c.productId, name: "" },
    }));
  }
  return editCompatibility.filter((c) => c.machineId === machineId);
}
